/**
 * DeskShell - the computer-screen frame (V19.19).
 *
 *   A menu down the left (brand on top, the open screen highlighted, its
 *   sub-pages under it) and a bar across the top (where you are, one search
 *   over lists and recipes, the reminders bell, "+ New list").
 *
 *   Rendered ONLY when useIsDesk() is true - see ProtectedRoute in App.js.
 *   On a phone this file is never mounted and the bottom menu is shown
 *   exactly as before.
 *
 *   SAME APP, DIFFERENT ARRANGEMENT. Nothing here is a second copy of a
 *   screen: every entry navigates to the route the phone already uses, the
 *   bell is the same Reminders page, "+ New list" opens the Lists page's own
 *   create box, and search opens a list or recipe the way the phone does.
 *   Search uses the two GETs the Lists and Recipes pages already make; no
 *   new server call.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShoppingCart, ChefHat, Calendar, BarChart3, Settings, Bell, Plus, Search, ChevronRight,
  Monitor, Smartphone, Keyboard, X,
} from "lucide-react";
import { useTranslation } from "@/i18n";
import { API } from "../../contexts/AuthContext";
import {
  PREVIEW_FRAME_NAME, PREVIEW_CHANGED, PREVIEW_SIZES, previewEnabled, previewScale,
} from "../../utils/phonePreview";

// The menu. Each sub-page is a route the phone already has and already
// reaches from inside its section (Reminders from Lists, Price Comparison
// from Stats, the rest from Settings).
const SECTIONS = [
  { path: "/", icon: ShoppingCart, key: "nav.lists", testId: "desk-nav-lists",
    match: (p) => p === "/" || p.startsWith("/list/"),
    subs: [{ path: "/reminders", key: "settings.reminders", testId: "desk-nav-reminders" }] },
  { path: "/recipes", icon: ChefHat, key: "nav.recipes", testId: "desk-nav-recipes",
    match: (p) => p.startsWith("/recipes"), subs: [] },
  { path: "/meal-planning", icon: Calendar, key: "nav.meals", testId: "desk-nav-meals",
    match: (p) => p.startsWith("/meal-planning"), subs: [] },
  { path: "/analytics", icon: BarChart3, key: "nav.stats", testId: "desk-nav-stats",
    match: (p) => p.startsWith("/analytics"),
    subs: [{ path: "/price-comparison", key: "priceComparison.title", testId: "desk-nav-prices" }] },
  { path: "/settings", icon: Settings, key: "nav.settings", testId: "desk-nav-settings",
    match: (p) => p === "/settings",
    subs: [
      { path: "/stores", key: "manageStores.title", testId: "desk-nav-stores" },
      { path: "/settings/favourites", key: "favourites.title", testId: "desk-nav-favourites" },
      { path: "/custom-barcodes", key: "customBarcodes.title", testId: "desk-nav-barcodes" },
    ] },
];

// Screens that read better as one narrow centred column (forms and
// settings), like Tradie's Bay's 720 px Settings-type screens.
const NARROW = ["/settings", "/stores", "/settings/favourites", "/custom-barcodes", "/reminders"];

export const sectionFor = (pathname) => {
  for (const s of SECTIONS) {
    const sub = s.subs.find((x) => pathname === x.path || pathname.startsWith(x.path + "/"));
    if (sub) return { section: s, sub };
    if (s.match(pathname)) return { section: s, sub: null };
  }
  return { section: null, sub: null };
};

export const isNarrowPage = (pathname) => NARROW.includes(pathname);

const norm = (s) => String(s || "").toLocaleLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");

function DeskSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ lists: [], recipes: [] });
  const [hi, setHi] = useState(0);
  const loadedAt = useRef(0);
  const box = useRef(null);
  const input = useRef(null);

  const load = async () => {
    // Fresh enough for a search box: re-read at most once a minute.
    if (Date.now() - loadedAt.current < 60000) return;
    loadedAt.current = Date.now();
    const [l, r] = await Promise.allSettled([
      axios.get(`${API}/lists?slim=true`),
      axios.get(`${API}/recipes`),
    ]);
    setData({
      lists: l.status === "fulfilled" && Array.isArray(l.value.data) ? l.value.data : [],
      recipes: r.status === "fulfilled" && Array.isArray(r.value.data) ? r.value.data : [],
    });
  };

  const results = useMemo(() => {
    const n = norm(q.trim());
    if (!n) return [];
    const lists = data.lists.filter((x) => norm(x.name).includes(n)).slice(0, 6)
      .map((x) => ({ kind: "list", id: x.id, name: x.name }));
    const recipes = data.recipes.filter((x) => norm(x.name).includes(n)).slice(0, 6)
      .map((x) => ({ kind: "recipe", id: x.id, name: x.name }));
    return [...lists, ...recipes];
  }, [q, data]);

  useEffect(() => { setHi(0); }, [q]);

  // Ctrl/Cmd+K or "/" jumps to the search box (not while typing elsewhere).
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      const typing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable;
      if ((e.key === "k" && (e.ctrlKey || e.metaKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const go = (r) => {
    if (!r) return;
    setOpen(false);
    setQ("");
    input.current?.blur();
    if (r.kind === "list") navigate(`/list/${r.id}`);
    else navigate("/recipes", { state: { openRecipeId: r.id } });
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[hi]); }
    else if (e.key === "Escape") { setOpen(false); e.currentTarget.blur(); }
  };

  const lists = results.filter((r) => r.kind === "list");
  const recipes = results.filter((r) => r.kind === "recipe");
  const row = (r) => {
    const i = results.indexOf(r);
    return (
      <button
        key={`${r.kind}-${r.id}`}
        type="button"
        className={`desk-search-row${i === hi ? " is-hi" : ""}`}
        onMouseEnter={() => setHi(i)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => go(r)}
        data-testid={`desk-search-${r.kind}-${r.id}`}
      >
        {r.kind === "list" ? <ShoppingCart size={16} /> : <ChefHat size={16} />}
        <span className="desk-search-name">{r.name}</span>
      </button>
    );
  };

  return (
    <div className="desk-search" ref={box}>
      <Search size={18} className="desk-search-icon" aria-hidden="true" />
      <input
        ref={input}
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); load(); }}
        onKeyDown={onKeyDown}
        placeholder={t("desk.searchPlaceholder")}
        aria-label={t("desk.searchPlaceholder")}
        data-testid="desk-search-input"
      />
      {open && q.trim() && (
        <div className="desk-search-pop" data-testid="desk-search-results">
          {results.length === 0 && <div className="desk-search-empty">{t("desk.noMatches")}</div>}
          {lists.length > 0 && <div className="desk-search-head">{t("nav.lists")}</div>}
          {lists.map(row)}
          {recipes.length > 0 && <div className="desk-search-head">{t("nav.recipes")}</div>}
          {recipes.map(row)}
        </div>
      )}
    </div>
  );
}

// Phone preview (V19.19, part 4): this same app in a phone-sized frame over
// the page area. The frame opens on the screen you are on; switching back to
// Computer opens the screen the frame was on. See utils/phonePreview.js for
// what the copy inside the frame does not do.
function PhoneStage({ src, frameRef }) {
  const { t } = useTranslation();
  const [size, setSize] = useState(PREVIEW_SIZES[1]);
  const [room, setRoom] = useState(() => window.innerHeight - 64 - 96);
  useEffect(() => {
    const on = () => setRoom(window.innerHeight - 64 - 96);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const scale = previewScale(size, room);
  return (
    <div className="desk-phone-stage" data-testid="phone-preview">
      <div className="desk-phone-sizes" role="group" aria-label={t("desk.phoneWidth")}>
        {PREVIEW_SIZES.map((sz) => (
          <button
            key={sz.w}
            type="button"
            className={sz.w === size.w ? "is-on" : ""}
            onClick={() => setSize(sz)}
            aria-pressed={sz.w === size.w}
            data-testid={`phone-size-${sz.w}`}
          >
            {sz.w}
          </button>
        ))}
      </div>
      <div className="desk-phone-hold" style={{ width: size.w * scale, height: size.h * scale }}>
        <div className="desk-phone-device" style={{ width: size.w, height: size.h, transform: `scale(${scale})` }}>
          <iframe
            ref={frameRef}
            name={PREVIEW_FRAME_NAME}
            src={src}
            title={t("desk.phone")}
            width={size.w}
            height={size.h}
            data-testid="phone-preview-frame"
          />
        </div>
      </div>
    </div>
  );
}

// "?" shows the keyboard shortcuts (V19.21 - shortcuts you can discover,
// not ones you have to be told about). The keyboard button in the top bar
// opens the same box.
function ShortcutsHelp({ open, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const rows = [
    [["Ctrl", "K"], t("desk.searchPlaceholder")],
    [["/"], t("desk.searchPlaceholder")],
    [[t("desk.rightClickKey")], t("desk.rightClick")],
    [["?"], t("desk.shortcuts")],
    [["Esc"], t("common.close")],
  ];
  return (
    <div className="dk-modal-back" onMouseDown={onClose} data-testid="desk-shortcuts-backdrop">
      <div className="dk-modal" role="dialog" aria-modal="true" aria-label={t("desk.shortcuts")} onMouseDown={(e) => e.stopPropagation()} data-testid="desk-shortcuts-box">
        <div className="dk-modal-head">
          <h2>{t("desk.shortcuts")}</h2>
          <button type="button" className="dk-icon-btn" onClick={onClose} aria-label={t("common.close")}><X size={18} /></button>
        </div>
        <dl className="dk-keys">
          {rows.map(([keys, what], i) => (
            <div key={i} className="dk-keys-row">
              <dt>{keys.map((k, j) => <kbd key={j}>{k}</kbd>)}</dt>
              <dd>{what}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

export function DeskShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { section, sub } = sectionFor(pathname);
  const [previewOn, setPreviewOn] = useState(previewEnabled);
  const [mode, setMode] = useState("computer");
  const [frameSrc, setFrameSrc] = useState(null);
  const [keysOpen, setKeysOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      const typing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable;
      if (e.key === "?" && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); setKeysOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const frameRef = useRef(null);
  useEffect(() => {
    const on = () => {
      const enabled = previewEnabled();
      setPreviewOn(enabled);
      if (!enabled) setMode("computer");
    };
    window.addEventListener(PREVIEW_CHANGED, on);
    window.addEventListener("storage", on);
    return () => { window.removeEventListener(PREVIEW_CHANGED, on); window.removeEventListener("storage", on); };
  }, []);
  const toPhone = () => { setFrameSrc(pathname + (search || "")); setMode("phone"); };
  const toComputer = () => {
    // Carry the screen the frame is on back to the computer.
    try {
      const loc = frameRef.current && frameRef.current.contentWindow && frameRef.current.contentWindow.location;
      const here = loc ? loc.pathname + loc.search : null;
      if (here && here !== pathname + (search || "")) navigate(here);
    } catch { /* noop */ }
    setMode("computer");
  };

  // The page-width rules in the "ON A COMPUTER" CSS key on these body
  // classes. They are removed when the shell unmounts (sign-out, or the
  // window narrowed below 1024 px), so Login and the phone layout are
  // never touched by them.
  useEffect(() => {
    document.body.classList.add("desk-shell");
    return () => document.body.classList.remove("desk-shell", "desk-narrow");
  }, []);
  useEffect(() => {
    document.body.classList.toggle("desk-narrow", isNarrowPage(pathname));
  }, [pathname]);

  return (
    <>
      <aside className="desk-nav" data-testid="desk-nav">
        <button type="button" className="desk-brand" onClick={() => navigate("/")} data-testid="desk-brand">
          <ShoppingCart size={22} strokeWidth={2} aria-hidden="true" />
          <span>Xelanti</span>
        </button>
        <nav className="desk-nav-items">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const here = section === s;
            const exact = here && !sub;
            return (
              <div key={s.path} className={`desk-nav-group${here ? " is-open" : ""}`}>
                <button
                  type="button"
                  className={`desk-nav-item${exact ? " is-active" : ""}${here ? " is-here" : ""}`}
                  onClick={() => navigate(s.path)}
                  aria-current={exact ? "page" : undefined}
                  data-testid={s.testId}
                >
                  <Icon size={20} strokeWidth={exact ? 2.2 : 1.7} aria-hidden="true" />
                  <span>{t(s.key)}</span>
                </button>
                {s.subs.map((x) => {
                  const on = sub === x;
                  return (
                    <button
                      key={x.path}
                      type="button"
                      className={`desk-nav-sub${on ? " is-active" : ""}`}
                      onClick={() => navigate(x.path)}
                      aria-current={on ? "page" : undefined}
                      data-testid={x.testId}
                    >
                      <span>{t(x.key)}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>

      <header className="desk-top" data-testid="desk-top">
        <div className="desk-crumbs" data-testid="desk-crumbs">
          {section && (
            sub ? (
              <>
                <button type="button" className="desk-crumb-link" onClick={() => navigate(section.path)}>{t(section.key)}</button>
                <ChevronRight size={16} className="desk-crumb-sep" aria-hidden="true" />
                <span className="desk-crumb-here">{t(sub.key)}</span>
              </>
            ) : (
              <span className="desk-crumb-here">{t(section.key)}</span>
            )
          )}
        </div>
        <DeskSearch />
        <div className="desk-top-actions">
          {previewOn && (
            <div className="desk-view-switch" role="group" aria-label={t("desk.phonePreview")} data-testid="desk-view-switch">
              <button type="button" className={mode === "computer" ? "is-on" : ""} aria-pressed={mode === "computer"}
                onClick={toComputer} data-testid="desk-view-computer">
                <Monitor size={16} aria-hidden="true" /><span>{t("desk.computer")}</span>
              </button>
              <button type="button" className={mode === "phone" ? "is-on" : ""} aria-pressed={mode === "phone"}
                onClick={toPhone} data-testid="desk-view-phone">
                <Smartphone size={16} aria-hidden="true" /><span>{t("desk.phone")}</span>
              </button>
            </div>
          )}
          <button
            type="button"
            className="desk-bell"
            onClick={() => setKeysOpen(true)}
            title={t("desk.shortcuts")}
            aria-label={t("desk.shortcuts")}
            data-testid="desk-keys-btn"
          >
            <Keyboard size={19} />
          </button>
          <button
            type="button"
            className={`desk-bell${pathname === "/reminders" ? " is-active" : ""}`}
            onClick={() => navigate("/reminders")}
            title={t("settings.reminders")}
            aria-label={t("settings.reminders")}
            data-testid="desk-bell"
          >
            <Bell size={20} />
          </button>
          {/* On Lists the page header has its own New list button. */}
          {pathname !== "/" && (
          <button
            type="button"
            className="desk-new"
            onClick={() => navigate("/", { state: { openCreate: Date.now() } })}
            data-testid="desk-new-list"
          >
            <Plus size={18} strokeWidth={2.4} aria-hidden="true" />
            <span>{t("listsPage.newList")}</span>
          </button>
          )}
        </div>
      </header>
      {previewOn && mode === "phone" && frameSrc && <PhoneStage src={frameSrc} frameRef={frameRef} />}
      <ShortcutsHelp open={keysOpen} onClose={() => setKeysOpen(false)} />
    </>
  );
}

export default DeskShell;
